<template>
  <form @submit.prevent="submit">
    <div class="form-item">
      <div :class="{ loading: loading }" class="license-check">
        <license-plate
          :id="`license-plate-input-${carIndex}`"
          :license-plate="licensePlate"
          :read-only="readOnly"
          :car-index="carIndex"
          @update-license-plate="updateLicensePlate"
        />

        <app-button
          v-if="!carDetails.ModelName && !loading && !readOnly"
          :disabled="!validLicensePlate"
          :show-icon="false"
          type="submit"
          button-type="cta"
          :data-qa="`search-exchange-car-license-plate-${carIndex}`"
        >
          {{ i18n.t('/ExchangeCar/SubmitTitle') }}
        </app-button>

        <div v-if="carDetails.ModelName || loading" class="car-details">
          <loader v-if="loading" />
          <car-details
            v-if="carDetails.ModelName"
            :details="carDetails"
            :read-only="readOnly"
            :car-index="carIndex"
            @update-license-plate="updateLicensePlate"
          />
        </div>
      </div>

      <alert v-if="showError" text="Kenteken niet gevonden" />
    </div>
  </form>
</template>

<script>
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import LicensePlate from '@/components/generic/presentational/molecules/licensePlate/LicensePlate.vue'
import CarDetails from '@/components/generic/presentational/molecules/carDetails/CarDetails.vue'
import Loader from '@/components/generic/presentational/atoms/loader/Loader.vue'
import Alert from '@/components/generic/presentational/atoms/alert/Alert.vue'
import Vehicle from 'api-client/Vehicle'
import { getUnknownExchangeCar, mapVehicleInfoToExchangeCar } from '@/mappers/ExchangeCarMapper'

export default {
  components: {
    AppButton,
    LicensePlate,
    CarDetails,
    Loader,
    Alert,
  },
  props: {
    carDetails: {
      type: Object,
      required: true,
    },
    carIndex: {
      type: Number,
      required: true,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['car-fetched', 'car-cleared'],
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      loading: false,
      showError: false,
      validLicensePlate: false,
      licensePlate: null,
    }
  },
  watch: {
    carDetails: {
      handler(newVal) {
        if (newVal.LicensePlate != null) {
          this.licensePlate = newVal.LicensePlate
        }
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    updateLicensePlate(licensePlate, validLicensePlate) {
      this.showError = false
      this.validLicensePlate = validLicensePlate
      this.licensePlate = licensePlate

      if (!licensePlate) {
        this.clearLicensePlate()
      } else if (validLicensePlate && !this.carDetails.ModelName && !this.loading) {
        this.submit()
      }
    },
    clearLicensePlate() {
      this.licensePlate = null
      this.showError = false
      this.$emit('car-cleared')
    },
    async submit() {
      this.loading = true
      this.showError = false

      try {
        if (!this.licensePlate) {
          console.warn('License plate was null or not defined')
          return
        }

        const vehicleInfoResponse = await Vehicle.getVehicleInfo(this.licensePlate)
        const car = mapVehicleInfoToExchangeCar(vehicleInfoResponse.vehicle)

        this.$emit('car-fetched', car)
      } catch (error) {
        console.error('Error fetching car:', error)
        const car = getUnknownExchangeCar(this.licensePlate)
        this.$emit('car-fetched', car)
        this.showError = true
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
$arrowSize: 1em;

@mixin arrow {
  content: '';
  position: absolute;

  top: $arrowSize * 0.75;
  border-top: $arrowSize solid transparent;
  border-bottom: $arrowSize solid transparent;
  border-right: $arrowSize solid;
  border-left-width: 0;
  border-right-color: $gray-base;
  left: -$arrowSize;
}

.form-item {
  margin-bottom: 1em;
}

.license-check {
  margin-bottom: 1em;
  @include breakpoint(md) {
    display: flex;
    align-items: flex-start;
  }
}

button {
  position: relative;
  flex: 0 0 auto;
  margin-top: $gutter;

  @include breakpoint(md) {
    margin-top: 0.35em;
    margin-left: $gutter;
  }
}

.car-details {
  position: relative;
  background-color: $white;
  border: solid 1px $gray-base;
  border-radius: 0.25em;
  margin-left: 2em;
  padding: 1em;
  min-width: 25em;

  &::before {
    @include arrow;

    border-right-color: $gray-base;
    left: -$arrowSize;
  }

  &::after {
    @include arrow;

    border-right-color: $white;
    left: calc(#{-$arrowSize} + 1px);
  }
}
</style>
